import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { sortCuratedLists } from "../watchlistFormat";

// Section label — same grey/weight as the settings headers, literal casing so
// the QUSHi wordmark keeps its lowercase i.
function SectionLabel({ children, action }) {
  return (
    <div className="mb-2 mt-6 flex items-center justify-between first:mt-0">
      <p className="text-[11px] font-semibold tracking-[0.14em] text-tertiary">{children}</p>
      {action}
    </div>
  );
}

// Preview line: up to 3 tickers + total count, e.g. "AAPL · MSFT · NVDA · 12 assets".
// The count tells users there's more to see on tap.
function PreviewLine({ preview }) {
  const symbols = preview?.symbols || [];
  const count = preview?.count;
  const parts = [...symbols];
  if (typeof count === "number") parts.push(`${count} ${count === 1 ? "asset" : "assets"}`);
  if (!parts.length) return null;
  return <div className="mt-0.5 truncate text-xs text-gray-400">{parts.join(" · ")}</div>;
}

// Active-list treatment: pale green band + "Active" pill + helper (no ticker
// preview). Used for whichever list is active — personal OR curated.
function ActiveRow({ list, onOpen }) {
  return (
    <button
      type="button"
      id="watchlist-guide-active-list"
      onClick={() => onOpen(list.id)}
      className="-mx-4 block w-[calc(100%+2rem)] border-b border-gray-100 bg-veryLightGreen px-4 py-3 text-left"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[15px] font-bold text-primaryColor">{list.name}</span>
        <span className="shrink-0 rounded-full bg-primaryColor px-2 py-0.5 text-[10px] font-semibold text-white">
          Active
        </span>
      </div>
      <div className="mt-0.5 text-xs text-gray-500">Powers your Trade wheel</div>
    </button>
  );
}

// A non-active personal list row that can be reordered by long-press-drag.
function ReorderRow({ list, preview, onOpen }) {
  const controls = useDragControls();
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const draggedRef = useRef(false);

  const clear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  const onPointerDown = (e) => {
    draggedRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };
    const nativeEvent = e.nativeEvent;
    timerRef.current = setTimeout(() => {
      draggedRef.current = true;
      controls.start(nativeEvent);
    }, 300);
  };
  const onPointerMove = (e) => {
    if (!startRef.current || draggedRef.current) return;
    if (Math.abs(e.clientX - startRef.current.x) > 8 || Math.abs(e.clientY - startRef.current.y) > 8) clear();
  };

  return (
    <Reorder.Item
      as="div"
      value={list}
      dragListener={false}
      dragControls={controls}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={clear}
      onPointerCancel={clear}
      onClick={() => {
        if (!draggedRef.current) onOpen(list.id);
      }}
      className="block w-full cursor-pointer select-none border-b border-gray-100 bg-white py-3"
      style={{ touchAction: "pan-y" }}
    >
      <div className="truncate text-[15px] font-bold text-primaryColor">{list.name}</div>
      <PreviewLine preview={preview} />
    </Reorder.Item>
  );
}

function StaticRow({ list, preview, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(list.id)}
      className="block w-full border-b border-gray-100 py-3 text-left"
    >
      <div className="truncate text-[15px] font-bold text-primaryColor">{list.name}</div>
      <PreviewLine preview={preview} />
    </button>
  );
}

// Screen 1 — flat list picker (home).
export default function MobileListPicker({
  userLists,
  curatedLists,
  previews,
  activeListId,
  onOpen,
  onCreate,
  onReorderLists,
}) {
  const activePersonal = userLists.find((l) => String(l.id) === String(activeListId));
  // Shared curated order (The Core pinned first) — see watchlistFormat.
  const orderedCurated = sortCuratedLists(curatedLists);
  const [order, setOrder] = useState([]);
  useEffect(() => {
    setOrder(userLists.filter((l) => String(l.id) !== String(activeListId)));
  }, [userLists, activeListId]);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleReorder = (newOrder) => {
    setOrder(newOrder);
    onReorderLists(activePersonal ? [activePersonal, ...newOrder] : newOrder);
  };

  const submitCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    const ok = await onCreate(name);
    setCreating(false);
    if (ok) {
      setName("");
      setShowCreate(false);
    }
  };

  const newAction = (
    <button
      type="button"
      id="watchlist-guide-new-button"
      onClick={() => setShowCreate(true)}
      className="text-sm font-semibold text-primaryColor"
    >
      + New
    </button>
  );

  return (
    <div className="w-full pb-6">
      {/* MY LISTS */}
      <SectionLabel action={newAction}>MY LISTS</SectionLabel>

      {/* Active personal list pinned first (only when the active list is personal) */}
      {activePersonal && <ActiveRow list={activePersonal} onOpen={onOpen} />}

      {/* Other personal lists (reorderable) */}
      {userLists.length === 0 ? (
        <p className="py-3 text-sm text-secondary">No personal watchlists yet</p>
      ) : (
        <Reorder.Group axis="y" values={order} onReorder={handleReorder} as="div">
          {order.map((list) => (
            <ReorderRow key={list.id} list={list} preview={previews[list.id]} onOpen={onOpen} />
          ))}
        </Reorder.Group>
      )}

      {/* QUSHi CURATED LISTS */}
      {/* Client Rev 2: "On desktop, there's some padding between the My List and
          Curated List sections, whereas on mobile they're all crammed together.
          Please add the same padding between the two sections on mobile."

          Desktop's ListPanel puts mt-6 on this same wrapper. Mobile had nothing,
          and SectionLabel's own mt-6 does not fill the gap on either breakpoint:
          the label is this div's FIRST child, so its `first:mt-0` cancels it.
          That is why desktop measured 24px of separation and mobile measured 0.
          Same class, same 24px — matched rather than invented. */}
      <div id="watchlist-guide-curated-section" className="mt-6">
        <SectionLabel>QUSHi CURATED LISTS</SectionLabel>
        <p className="-mt-1 mb-1 text-xs text-secondary">Ready-made lists for market context</p>
        <div id="watchlist-guide-curated-items">
          {orderedCurated.map((list) =>
            String(list.id) === String(activeListId) ? (
              <ActiveRow key={list.id} list={list} onOpen={onOpen} />
            ) : (
              <StaticRow key={list.id} list={list} preview={previews[list.id]} onOpen={onOpen} />
            )
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-bold text-[#004d4d]">Create List</h3>
            <input
              type="text"
              value={name}
              autoFocus
              maxLength={24}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitCreate()}
              placeholder="Enter watchlist name"
              className="mb-4 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-primaryColor focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreate}
                disabled={creating}
                className="rounded-lg bg-primaryColor px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
